import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import { ArrowRight, CheckCircle2, Zap, Home, Lightbulb, Shield, Settings, Wrench, Phone, Clock, Award, X, Plug, Cpu, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const elTranslations = {
  de: {
    badge: 'Professionelle Elektrotechnik',
    heroTitle: 'Elektro-',
    heroTitle2: 'installationen',
    heroDesc: 'Von der Hausinstallation bis zur Industrieelektrik – wir bieten das komplette Spektrum moderner Elektrotechnik. Sicher, zuverlässig und normgerecht.',
    freeConsult: 'Kostenlose Beratung',
    services: 'Leistungen',
    emergency: 'Notdienst',
    yearsExp: 'Jahre Erfahrung',
    certMaster: 'Meisterbetrieb',
    ourServices: 'Unsere Leistungen',
    ourServicesDesc: 'Professionelle Elektroinstallationen für jeden Bedarf',
    homeInstall: 'Hausinstallation',
    homeInstallDesc: 'Neuinstallation, Sanierung und Erweiterung Ihrer Elektroanlage',
    industrial: 'Industrieelektrik',
    industrialDesc: 'Professionelle Lösungen für Gewerbe und Industrie',
    smartHome: 'Smart Home',
    smartHomeDesc: 'Intelligente Haussteuerung und Automation',
    emergency24: 'Notdienst 24/7',
    emergencyDesc: 'Schnelle Hilfe bei elektrischen Notfällen',
    installation: 'Neuinstallation',
    renovation: 'Sanierung',
    expansion: 'Erweiterung',
    lighting: 'Beleuchtung',
    machineConn: 'Maschinenanschluss',
    controlSys: 'Steuerungstechnik',
    emergencyPower: 'Notstromversorgung',
    automation: 'Automation',
    voiceControl: 'Sprachsteuerung',
    energyMgmt: 'Energiemanagement',
    security: 'Sicherheitstechnik',
    fastResponse: 'Schnelle Reaktion',
    available247: 'Erreichbar 24/7',
    emerRepair: 'Notfallreparaturen',
    ready: 'Bereit für Ihr Elektroprojekt?',
    readyDesc: 'Kontaktieren Sie uns für ein kostenloses Angebot. Wir planen und realisieren Ihr Projekt termingerecht.'
  },
  en: {
    badge: 'Professional Electrical Engineering',
    heroTitle: 'Electrical',
    heroTitle2: 'Installations',
    heroDesc: 'From home installation to industrial electrical – we offer the complete spectrum of modern electrical engineering. Safe, reliable and compliant.',
    freeConsult: 'Free Consultation',
    services: 'Services',
    emergency: 'Emergency',
    yearsExp: 'Years Experience',
    certMaster: 'Certified Master',
    ourServices: 'Our Services',
    ourServicesDesc: 'Professional electrical installations for every need',
    homeInstall: 'Home Installation',
    homeInstallDesc: 'New installation, renovation and expansion of your electrical system',
    industrial: 'Industrial Electrical',
    industrialDesc: 'Professional solutions for commercial and industrial',
    smartHome: 'Smart Home',
    smartHomeDesc: 'Intelligent home control and automation',
    emergency24: 'Emergency 24/7',
    emergencyDesc: 'Fast help for electrical emergencies',
    installation: 'New Installation',
    renovation: 'Renovation',
    expansion: 'Expansion',
    lighting: 'Lighting',
    machineConn: 'Machine Connection',
    controlSys: 'Control Systems',
    emergencyPower: 'Emergency Power',
    automation: 'Automation',
    voiceControl: 'Voice Control',
    energyMgmt: 'Energy Management',
    security: 'Security Technology',
    fastResponse: 'Fast Response',
    available247: 'Available 24/7',
    emerRepair: 'Emergency Repairs',
    ready: 'Ready for Your Electrical Project?',
    readyDesc: 'Contact us for a free quote. We plan and implement your project on time.'
  }
};

const ElektrikHero = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = elTranslations[language] || elTranslations.de;

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Elektroinstallation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Zap className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 text-sm font-medium">{t.badge}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {t.heroTitle}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-sky-400">
                {t.heroTitle2}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => navigate('/beratung')}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-6 text-lg group shadow-xl shadow-amber-500/25"
              >
                {t.freeConsult}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => document.getElementById('leistungen').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                {t.services}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">24/7</div>
                <div className="text-sm text-gray-400">{t.emergency}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">25+</div>
                <div className="text-sm text-gray-400">{t.yearsExp}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">100%</div>
                <div className="text-sm text-gray-400">{t.certMaster}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section with Icons
const ElektrikServices = () => {
  const { language } = useLanguage();
  
  const services = language === 'en' ? [
    {
      icon: Lightbulb,
      title: 'Lighting',
      description: 'Modern LED systems, lighting design and installation for optimal illumination',
      color: 'from-amber-400 to-amber-600'
    },
    {
      icon: Home,
      title: 'Home Installations',
      description: 'Complete electrical installations for new buildings and renovations',
      color: 'from-sky-400 to-sky-600'
    },
    {
      icon: Cpu,
      title: 'Smart Home',
      description: 'Intelligent home control for lighting, heating, blinds and more',
      color: 'from-purple-400 to-purple-600'
    },
    {
      icon: Plug,
      title: 'E-Mobility',
      description: 'Wallbox installation and charging infrastructure for electric vehicles',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: Lock,
      title: 'Security Technology',
      description: 'Alarm systems, video surveillance and access control systems',
      color: 'from-red-400 to-red-600'
    },
    {
      icon: Zap,
      title: 'High Voltage',
      description: 'Industrial installations, distribution board construction and network technology',
      color: 'from-orange-400 to-orange-600'
    }
  ] : [
    {
      icon: Lightbulb,
      title: 'Beleuchtung',
      description: 'Moderne LED-Systeme, Lichtplanung und Installation für optimale Beleuchtung',
      color: 'from-amber-400 to-amber-600'
    },
    {
      icon: Home,
      title: 'Hausinstallationen',
      description: 'Komplette Elektroinstallationen für Neubauten und Renovierungen',
      color: 'from-sky-400 to-sky-600'
    },
    {
      icon: Cpu,
      title: 'Smart Home',
      description: 'Intelligente Haussteuerung für Licht, Heizung, Jalousien und mehr',
      color: 'from-purple-400 to-purple-600'
    },
    {
      icon: Plug,
      title: 'E-Mobilität',
      description: 'Wallbox-Installation und Ladeinfrastruktur für Elektrofahrzeuge',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: Lock,
      title: 'Sicherheitstechnik',
      description: 'Alarmanlagen, Videoüberwachung und Zutrittskontrollsysteme',
      color: 'from-red-400 to-red-600'
    },
    {
      icon: Zap,
      title: 'Starkstrom',
      description: 'Industrielle Installationen, Verteilerbau und Netzwerktechnik',
      color: 'from-orange-400 to-orange-600'
    }
  ];

  return (
    <section id="elektrik" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Our ' : 'Unsere '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Services' : 'Leistungen'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'Professional electrical installations for every need' : 'Professionelle Elektroinstallationen für jeden Bedarf'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 bg-white overflow-hidden">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Photo Gallery Section
const ElektrikGalerie = () => {
  const { language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  
  const photos = language === 'en' ? [
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qf7kwtp5_IMG_0124.jpeg',
      title: 'Professional Installation',
      category: 'Our Project'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/srw5f2ee_IMG_0120.jpeg',
      title: 'Switchboard Assembly',
      category: 'Our Project'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/4x1fasuz_IMG_0117.jpeg',
      title: 'Electrical Specialist',
      category: 'Our Team'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2os8hk5m_IMG_0118.jpeg',
      title: 'Precision Work',
      category: 'Our Project'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2moyjid2_IMG_0119.jpeg',
      title: 'Outlet Installation',
      category: 'Home Installation'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_227fe9ec-1a1f-4812-8ef5-359c5fdd5ffd/artifacts/4cm5wbcv_premium_photo-1688686804638-fadb460edc4a.avif',
      title: 'Modern Switchboard',
      category: 'Installation'
    }
  ] : [
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qf7kwtp5_IMG_0124.jpeg',
      title: 'Professionelle Installation',
      category: 'Unser Projekt'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/srw5f2ee_IMG_0120.jpeg',
      title: 'Schalttafel Montage',
      category: 'Unser Projekt'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/4x1fasuz_IMG_0117.jpeg',
      title: 'Elektrofachkraft',
      category: 'Unser Team'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2os8hk5m_IMG_0118.jpeg',
      title: 'Präzisionsarbeit',
      category: 'Unser Projekt'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2moyjid2_IMG_0119.jpeg',
      title: 'Steckdosen Installation',
      category: 'Hausinstallation'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_227fe9ec-1a1f-4812-8ef5-359c5fdd5ffd/artifacts/4cm5wbcv_premium_photo-1688686804638-fadb460edc4a.avif',
      title: 'Moderne Schalttafel',
      category: 'Installation'
    }
  ];

  return (
    <section id="galerie" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Our ' : 'Unsere '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Work' : 'Arbeiten'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'Insights into our professional electrical installations' : 'Einblicke in unsere professionellen Elektroinstallationen'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((photo, index) => (
            <div 
              key={index} 
              className="group cursor-pointer relative overflow-hidden rounded-xl shadow-lg"
              onClick={() => setSelectedImage(photo)}
            >
              <div className="aspect-[4/3]">
                <img 
                  src={photo.src} 
                  alt={photo.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-sky-400 text-sm font-medium">{photo.category}</span>
                  <h3 className="text-white text-xl font-bold">{photo.title}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button className="absolute top-4 right-4 text-white hover:text-gray-300">
              <X className="w-8 h-8" />
            </button>
            <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
              <img 
                src={selectedImage.src} 
                alt={selectedImage.title}
                className="w-full h-auto rounded-xl"
              />
              <div className="text-center mt-4">
                <span className="text-sky-400 text-sm font-medium">{selectedImage.category}</span>
                <h3 className="text-white text-2xl font-bold">{selectedImage.title}</h3>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Process Section
const ElektrikProzess = () => {
  const { language } = useLanguage();
  
  const steps = language === 'en' ? [
    {
      number: '01',
      title: 'Consultation',
      description: 'Free initial consultation and needs analysis on site',
      icon: Phone
    },
    {
      number: '02',
      title: 'Planning',
      description: 'Detailed planning and transparent quote',
      icon: Settings
    },
    {
      number: '03',
      title: 'Installation',
      description: 'Professional execution by certified electricians',
      icon: Wrench
    },
    {
      number: '04',
      title: 'Acceptance',
      description: 'Testing, documentation and instruction',
      icon: CheckCircle2
    }
  ] : [
    {
      number: '01',
      title: 'Beratung',
      description: 'Kostenlose Erstberatung und Bedarfsanalyse vor Ort',
      icon: Phone
    },
    {
      number: '02',
      title: 'Planung',
      description: 'Detaillierte Planung und transparentes Angebot',
      icon: Settings
    },
    {
      number: '03',
      title: 'Installation',
      description: 'Professionelle Ausführung durch zertifizierte Elektriker',
      icon: Wrench
    },
    {
      number: '04',
      title: 'Abnahme',
      description: 'Prüfung, Dokumentation und Einweisung',
      icon: CheckCircle2
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-sky-900 to-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {language === 'en' ? 'Our ' : 'Unser '}<span className="text-amber-400">{language === 'en' ? 'Process' : 'Ablauf'}</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {language === 'en' ? 'From consultation to completion - how we work' : 'Von der Beratung bis zur Fertigstellung - so arbeiten wir'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 h-full border border-white/20 hover:bg-white/15 transition-colors">
                  <div className="text-6xl font-bold text-white/20 mb-4">{step.number}</div>
                  <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-amber-500/50" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WarumWir = () => {
  const { language } = useLanguage();
  
  const reasons = language === 'en' ? [
    {
      icon: Award,
      title: 'Certified Professionals',
      description: 'All our electricians are trained and certified'
    },
    {
      icon: Clock,
      title: 'Fast Service',
      description: 'Short response times and on-schedule execution'
    },
    {
      icon: Shield,
      title: 'Warranty',
      description: 'Comprehensive warranty on all our work'
    },
    {
      icon: CheckCircle2,
      title: 'Fixed Price Guarantee',
      description: 'Transparent prices with no hidden costs'
    }
  ] : [
    {
      icon: Award,
      title: 'Zertifizierte Fachkräfte',
      description: 'Alle unsere Elektriker sind ausgebildet und zertifiziert'
    },
    {
      icon: Clock,
      title: 'Schneller Service',
      description: 'Kurze Reaktionszeiten und termingerechte Ausführung'
    },
    {
      icon: Shield,
      title: 'Garantie',
      description: 'Umfassende Garantie auf alle unsere Arbeiten'
    },
    {
      icon: CheckCircle2,
      title: 'Festpreisgarantie',
      description: 'Transparente Preise ohne versteckte Kosten'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {language === 'en' ? 'Why ' : 'Warum '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">Gravante?</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {language === 'en' 
                ? 'With over 25 years of experience in electrical installations, we are your reliable partner for all electrical work. Quality, safety and customer satisfaction are our top priorities.'
                : 'Mit über 25 Jahren Erfahrung im Bereich Elektroinstallationen sind wir Ihr zuverlässiger Partner für alle elektrischen Arbeiten. Qualität, Sicherheit und Kundenzufriedenheit stehen bei uns an erster Stelle.'}
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {reasons.map((reason, index) => {
                const Icon = reason.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{reason.title}</h3>
                      <p className="text-sm text-gray-600">{reason.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_227fe9ec-1a1f-4812-8ef5-359c5fdd5ffd/artifacts/9t3jkpqn_istockphoto-2192839882-612x612.webp"
                alt="Smart Home System"
                className="rounded-2xl shadow-xl w-full h-48 object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_227fe9ec-1a1f-4812-8ef5-359c5fdd5ffd/artifacts/szrrscwf_photo-1730967844913-29eb5cae5f34.avif"
                alt="Moderne Elektroinstallation"
                className="rounded-2xl shadow-xl w-full h-48 object-cover mt-8"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_227fe9ec-1a1f-4812-8ef5-359c5fdd5ffd/artifacts/1hrkhg6q_photo-1558002038-1055907df827.avif"
                alt="Haus Elektrik"
                className="rounded-2xl shadow-xl w-full h-48 object-cover -mt-8"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_227fe9ec-1a1f-4812-8ef5-359c5fdd5ffd/artifacts/9t3jkpqn_istockphoto-2192839882-612x612.webp"
                alt="Smart Home"
                className="rounded-2xl shadow-xl w-full h-48 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const ElektrikCTA = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  return (
    <section className="py-20 bg-gradient-to-r from-sky-600 to-sky-700">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Zap className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            {language === 'en' ? 'Planning an Electrical Installation?' : 'Elektroinstallation geplant?'}
          </h2>
          <p className="text-xl mb-8 leading-relaxed opacity-90">
            {language === 'en' 
              ? 'Contact us for a free consultation. We will provide you with a non-binding quote.'
              : 'Kontaktieren Sie uns für eine kostenlose Beratung. Wir erstellen Ihnen ein unverbindliches Angebot.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/beratung')}
              size="lg"
              className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold shadow-xl"
            >
              {language === 'en' ? 'Request Now' : 'Jetzt anfragen'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="tel:+4915782283963"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg text-lg font-medium transition-colors"
            >
              <Phone className="w-5 h-5" />
              +49 157 82283963
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const ElektrikPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <ElektrikHero />
      <ElektrikServices />
      <ElektrikGalerie />
      <ElektrikProzess />
      <WarumWir />
      <ElektrikCTA />
      <Contact />
      <Footer />
    </div>
  );
};

export default ElektrikPage;
